import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import { useAuth } from '../contexts/AuthContext';
import CustomerDashboard from './dashboards/CustomerDashboard';
import MerchantDashboard from './dashboards/MerchantDashboard';
import DeliveryDashboard from './dashboards/DeliveryDashboard';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  People as PeopleIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import axios from '../api';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar } from 'recharts';
import LoadingAnimation from '../components/LoadingAnimation';
import PageHeader from '../components/ui/PageHeader';


interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 500 }}>
          {title}
        </Typography>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            bgcolor: '#F1F1EC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& > *': { fontSize: 19, color: color },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography component="div" className="tabular-nums" sx={{ fontWeight: 700, fontSize: 28, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
        {value}
      </Typography>
      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <TrendingUpIcon sx={{ fontSize: 15, color: 'success.main' }} />
          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, fontSize: 12.5 }}>
            {trend}
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('Weekly');
  const [error, setError] = useState('');

  useEffect(() => {
    // Only load admin stats if user is admin
    if (user?.role !== 'admin') return;

    const loadStats = async () => {
      try {
        const response = await axios.get('/api/admin/dashboard/stats', { params: { period } });
        setStats(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching stats:', error);
        setError('Unable to load dashboard data. Check the backend and MongoDB connection.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [period, user?.role]);

  // Route to role-specific dashboard (after hooks)
  if (user?.role === 'customer') {
    return <CustomerDashboard />;
  }
  if (user?.role === 'merchant') {
    return <MerchantDashboard />;
  }
  if (user?.role === 'delivery') {
    return <DeliveryDashboard />;
  }
  // Admin continues to default dashboard below

  if (loading) {
    return <LoadingAnimation message="Loading your dashboard..." subtitle="Fetching fresh data from BiteHub" />;
  }

  if (error || !stats) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error || 'No dashboard data available.'}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Make sure the backend server is running on port 5001 and MongoDB is connected.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Good morning, Admin"
        subtitle={`${stats.todayOrders.toLocaleString()} orders today \u00B7 OMR ${stats.revenue.today.toLocaleString()} settled \u00B7 ${stats.rescuedBoxes.toLocaleString()} boxes rescued all-time`}
        actions={
          <ToggleButtonGroup value={period} exclusive onChange={(_, value) => value && setPeriod(value)} size="small" aria-label="Dashboard period">
            {['Daily', 'Weekly', 'Monthly'].map((option) => <ToggleButton key={option} value={option}>{option}</ToggleButton>)}
          </ToggleButtonGroup>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <StatCard
          title="Customers"
          value={(stats.totalCustomers || stats.totalUsers - (stats.totalBusinesses || 0) - 1).toLocaleString()}
          icon={<PeopleIcon />}
          color="#0B6E4F"
          trend="+12.5% this month"
        />
        <StatCard
          title="Active Merchants"
          value={stats.totalBusinesses.toLocaleString()}
          icon={<StoreIcon />}
          color="#B1D65D"
          trend="+8.2% this month"
        />
        <StatCard
          title="Active Surprise Boxes"
          value={stats.totalSurpriseBoxes.toLocaleString()}
          icon={<InventoryIcon />}
          color="#FF6B35"
          trend="+15.3% this week"
        />
        <StatCard
          title="Orders Today"
          value={stats.todayOrders.toLocaleString()}
          icon={<ShoppingCartIcon />}
          color="#2196F3"
          trend="+20.1% today"
        />
        <StatCard title="Revenue Today" value={`OMR ${stats.revenue.today.toLocaleString()}`} icon={<TrendingUpIcon />} color="#8FB946" trend="+9.4% today" />
        <StatCard title="Food Rescued" value={`${stats.todayOrders.toLocaleString()} boxes`} icon={<InventoryIcon />} color="#FF6B35" trend="+18.7% this month" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mb: 3 }}>
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Orders & revenue</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{period} performance</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={stats.ordersTrend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs><linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0B6E4F" stopOpacity={0.25} /><stop offset="95%" stopColor="#0B6E4F" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E8E5" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip formatter={(value, name) => [name === 'revenue' ? `OMR ${value}` : value, name === 'revenue' ? 'Revenue' : 'Orders']} />
                  <Area type="monotone" dataKey="orders" stroke="#0B6E4F" strokeWidth={3} fill="url(#ordersFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Recent Activity
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Activity</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recentActivity.map((activity: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={activity.type.replace('_', ' ')}
                            size="small"
                            color={
                              activity.type === 'user_registered'
                                ? 'primary'
                                : activity.type === 'order_placed'
                                ? 'success'
                                : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {activity.user || activity.order || activity.business}
                        </TableCell>
                        <TableCell>{activity.time}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Food rescue impact</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.rescuedBoxes.toLocaleString()}</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>boxes rescued all time</Typography>
              <ResponsiveContainer width="100%" height={100}>
                <BarChart data={stats.ordersTrend}>
                  <Bar dataKey="orders" fill="#B1D65D" radius={[4, 4, 0, 0]} />
                  <XAxis dataKey="name" hide /><YAxis hide /><Tooltip />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Revenue
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography color="text.secondary" variant="body2">
                  Today
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  <RialSymbol /> {stats.revenue.today.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography color="text.secondary" variant="body2">
                  This Month
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                  <RialSymbol /> {stats.revenue.month.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  <RialSymbol /> {stats.revenue.total.toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Stats
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Active Users</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{stats.activeUsers}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Pending Businesses</Typography>
                  <Typography sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {stats.pendingBusinesses}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Today's Orders</Typography>
                  <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                    {stats.todayOrders}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
